import { BankDetail, Proposal, ReducedProposal} from "@/models/proposal.model";
import { axiosInstance } from "./axiosInstance";

type GetProposalsParams = {
  page: number;
  pageSize: number;
  proposalId?: string;
  name?: string;
  cpf?: string;
  sellerName?: string;
  releasedValue?: number;
  proposalDate?: string;
  disbursementDate?: string;
  statuses?: string[];
  sortBy?: string;
  sortDirection?: string;
  origin?: string;
  searchTerm?: string;
  startDate?: Date | string | null;
  endDate?: Date | string | null;
  corbanId?: string | number;
};

export const getProposals = async ({
  page,
  pageSize,
  proposalId,
  name,
  cpf,
  sellerName,
  releasedValue,
  proposalDate,
  disbursementDate,
  statuses,
  sortBy,
  sortDirection,
  origin,
  searchTerm,
  startDate,
  endDate,
}: GetProposalsParams): Promise<{ data: Proposal[]; totalPages: number }> => {
  try {
    const response = await axiosInstance.get<{
      status: "success" | "error";
      data: Proposal[];
      totalPages: number;
      page: number;
      pageSize: number;
    }>(`/api/proposal`, {
      params: {
        page,
        pageSize,
        proposalId,
        name,
        cpf,
        sellerName,
        releasedValue,
        proposalDate,
        disbursementDate,
        statuses,
        sortBy,
        sortDirection,
        origin,
        searchTerm,
        startDate,
        endDate,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Erro ao obter Propostas:", error);
    throw error;
  }
};
export const getProposalById = async (id: string): Promise<Proposal | null> => {
  const response = await axiosInstance.get(`api/proposal/${id}`);

  return response.data;
};

export const updateProposalBankDetail = async (
  bankDetailData: Partial<BankDetail> & { id?: string | number }
): Promise<BankDetail> => {
  try
  {
    const { id, ...data } = bankDetailData;

    if (!id)
    {
      throw new Error(
        "O ID da proposta é obrigatório para atualizar os detalhes bancários."
      );
    }

    const response = await axiosInstance.put<BankDetail>(
      `/api/proposal/${id}/bank-detail`,
      data
    );
    return response.data;
  } catch (error)
  {
    console.error("Erro ao atualizar detalhes bancários da proposta:", error);
    throw error;
  }
};

export const getProposalsV2 = async ({
  page,
  pageSize,
  proposalId,
  name,
  cpf,
  sellerName,
  releasedValue,
  proposalDate,
  disbursementDate,
  statuses,
  sortBy,
  sortDirection,
  origin,
  searchTerm,
  startDate,
  endDate,
}: GetProposalsParams): Promise<{ data: ReducedProposal[]; totalPages: number }> => {
  try {
    const response = await axiosInstance.get<{
      status: "success" | "error";
      data: ReducedProposal[];
      totalPages: number;
      page: number;
      pageSize: number;
    }>(`/api/v2/proposal`, {
      params: {
        page,
        pageSize,
        proposalId,
        name,
        cpf,
        sellerName,
        releasedValue,
        proposalDate,
        disbursementDate,
        statuses,
        sortBy,
        sortDirection,
        origin,
        searchTerm,
        startDate,
        endDate,
      },
    });

    return response.data;
  } catch (error) {
    console.error("Erro ao obter Propostas:", error);
    throw error;
  }
};


export const getProposalByBalanceSolicitationId = async (
  balanceSolicitationId: string
): Promise<Proposal | null> => {
  if (!balanceSolicitationId) return null;
  try {
    const proposalRes = await axiosInstance.get(
      `/api/proposal/balance-solicitation/${balanceSolicitationId}`
    );

    return proposalRes.data;
  } catch (error) {
    console.error("Erro ao obter o Proposta:", error);
    throw error;
  }
};

export const updateProposal = async (
  proposalData: Partial<Proposal>
): Promise<Proposal> => {
  try {
    const response = await axiosInstance.put<Proposal>(
      `/api/proposal/${proposalData.id}`,
      proposalData
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao criar proposta:", error);
    throw error;
  }
};

export const rejectProposal = async (id: string): Promise<Proposal> => {
  try {
    const response = await axiosInstance.post<Proposal>(
      `/api/proposal/${id}/cancel`
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao cancelar proposta:", error);
    throw error;
  }
};

export const approveProposal = async (id: string): Promise<Proposal> => {
  try {
    const response = await axiosInstance.post<Proposal>(
      `/api/proposal/${id}/approve-formalization`
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao aprovar proposta:", error);
    throw error;
  }
};

export const updateProposalCustomStatus = async (
  id: string,
  customStatus: string
): Promise<Proposal> => {
  try {
    const response = await axiosInstance.put<Proposal>(
      `/api/proposal-after-formalization/${id}`,
      { customStatus }
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao atualizar status da proposta:", error);
    throw error;
  }
};

export const forceProposal = async (id: string): Promise<Proposal> => {
  try {
    const response = await axiosInstance.put<Proposal>(
      `/api/force-proposal/${id}`
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao criar proposta:", error);
    throw error;
  }
};

export const retryCreateProposalBank = async (
  id: string,
  retryProposal: object
): Promise<Proposal | null> => {
  try
  {
    const response = await axiosInstance.post<Proposal>(
      `/api/v2/proposal/${id}/retry-create-proposal`, retryProposal
    );
    return response.data;
  } catch (error)
  {
    console.error("Erro ao tentar recriar proposta:", error);
    throw error;
  }
};

export const changeUnicoProcess = async (
  proposalId: string
): Promise<Proposal> => {
  try {
    const response = await axiosInstance.put<Proposal>(
      `/api/proposal/unico-data`,
      { proposalId }
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao trocar processo único:", error);
    throw error;
  }
};

export const changeUnicoProcessCorrections = async (
  proposalId: string,
  naturalPerson: {
    document?: string;
    name?: string | null;
    birthDate?: string | null;
  }
): Promise<Proposal> => {
  try {
    const response = await axiosInstance.post<Proposal>(
      `/api/v2/bio-validation`,
      { proposalId, naturalPerson }
    );
    return response.data;
  } catch (error) {
    console.error("Erro ao trocar processo único:", error);
    throw error;
  }
};

export const changeProposalStatusToPendingUserReview = async (
  proposalId: string
): Promise<Proposal> => {
  try {
    const response = await axiosInstance.put<Proposal>(
      `/api/proposal/update-status/`,
      { proposalId }
    );
    return response.data;
  } catch (error) {
    console.error("Error ao trocar o status da proposta.");
    throw error;
  }
};

export const updateOnlyStatus = async (
  proposalId: string,
  status: string
): Promise<Proposal> => {
  try {
    const response = await axiosInstance.put<Proposal>(
      `/api/proposal/update-only-status/`,
      { proposalId, status }
    );
    return response.data;
  } catch (error) {
    console.error("Error ao trocar o status da proposta.");
    throw error;
  }
};

export const updateProposalDisbursementDate = async (
  proposalId: string,
  date: Date
): Promise<Proposal> => {
  try {
    const response = await axiosInstance.put<Proposal>(
      `/api/proposal/update-disbursementDate`,
      { proposalId, date }
    );
    return response.data;
  } catch (error) {
    console.error("Error ao trocar o status da proposta.");
    throw error;
  }
};

export const updateExternalId = async (
  proposalId: string,
  externalId: string
): Promise<Proposal> => {
  try {
    const response = await axiosInstance.put<Proposal>(
      `/api/proposal/update-externalId`,
      { proposalId, externalId }
    );
    return response.data;
  } catch (error) {
    console.error("Error ao trocar o status da proposta.");
    throw error;
  }
};
